import { useEffect, useState } from "react";
import { CompanyTabs } from "./components/CompanyTabs.js";
import {
  DEFAULT_DOCUMENT_TYPES,
  GlobalFilters,
  type GlobalFiltersValue,
} from "./components/GlobalFilters.js";
import { InvoicesTable } from "./components/InvoicesTable.js";
import { ScanDialog } from "./components/ScanDialog.js";
import { ScanHistoryDialog } from "./components/ScanHistoryDialog.js";
import { UsersDialog } from "./components/UsersDialog.js";
import { useCompanies } from "./hooks/useCompanies.js";
import { yearRange } from "./lib/filters.js";
import { Login } from "./pages/Login.js";
import { useAuth } from "./providers/AuthProvider.js";

export function App() {
  const { isAuthenticated, isLoading: authLoading, user, logout } = useAuth();
  const companies = useCompanies();
  const [activeCompany, setActiveCompany] = useState<string | null>(null);
  const [scanOpen, setScanOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [usersOpen, setUsersOpen] = useState(false);
  const currentYear = new Date().getUTCFullYear();
  const [filters, setFilters] = useState<GlobalFiltersValue>(() => ({
    ...yearRange(currentYear),
    year: currentYear,
    quarter: "all",
    supplier: "",
    documentTypes: DEFAULT_DOCUMENT_TYPES,
  }));

  // Auto-vybrat první firmu po načtení
  useEffect(() => {
    if (!activeCompany && companies.data && companies.data.length > 0) {
      setActiveCompany(companies.data[0]!.id);
    }
  }, [companies.data, activeCompany]);

  // Initial auth check běží — zatím nic nerendrujeme, ať neflashne login.
  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-slate-500">
        Načítám…
      </div>
    );
  }

  // Bez přihlášení zobrazíme jen login.
  if (!isAuthenticated) {
    return <Login />;
  }

  return (
    <div className="min-h-screen flex flex-col">
      <header className="px-6 py-4 bg-white border-b border-slate-200 flex justify-between items-center">
        <h1 className="text-xl font-bold">Invoice Processor</h1>
        <div className="flex items-center gap-3">
          <span className="text-sm text-slate-500">
            {user?.name || user?.email}
          </span>
          {user?.role === "superadmin" && (
            <button
              onClick={() => setUsersOpen(true)}
              className="px-3 py-1.5 text-sm text-slate-700 border border-slate-300 rounded hover:bg-slate-100"
            >
              Uživatelé
            </button>
          )}
          <button
            onClick={() => setHistoryOpen(true)}
            className="px-3 py-1.5 text-sm text-slate-700 border border-slate-300 rounded hover:bg-slate-100"
          >
            Historie
          </button>
          <button
            onClick={() => setScanOpen(true)}
            className="px-3 py-1.5 bg-blue-600 text-white rounded hover:bg-blue-700"
          >
            Spustit scan
          </button>
          <button
            onClick={() => void logout()}
            className="px-3 py-1.5 text-sm text-slate-600 border border-slate-300 rounded hover:bg-slate-100"
          >
            Odhlásit
          </button>
        </div>
      </header>

      <main className="flex-1 p-6 space-y-4">
        {companies.isLoading && <div>Načítám firmy…</div>}
        {companies.isError && (
          <div className="text-red-600">
            Chyba při načítání firem. Zkontroluj připojení k Hasuře.
          </div>
        )}
        {companies.data && companies.data.length > 0 && (
          <>
            <CompanyTabs
              companies={companies.data}
              active={activeCompany}
              onChange={setActiveCompany}
            />
            <GlobalFilters value={filters} onChange={setFilters} />
            {activeCompany && (
              <InvoicesTable companyId={activeCompany} filters={filters} />
            )}
          </>
        )}
      </main>

      <ScanDialog open={scanOpen} onClose={() => setScanOpen(false)} />
      <ScanHistoryDialog
        open={historyOpen}
        onClose={() => setHistoryOpen(false)}
      />
      {user?.role === "superadmin" && (
        <UsersDialog open={usersOpen} onClose={() => setUsersOpen(false)} />
      )}
    </div>
  );
}
